export type Locale = 'he' | 'en' | 'ru' | 'it' | 'uk'

export const LOCALES: readonly Locale[] = ['he', 'en', 'ru', 'it', 'uk']

/** The prototype's own default and this repo's `he-IL` pilot audience. */
export const DEFAULT_LOCALE: Locale = 'he'

/**
 * The narrowing every boundary that reads a locale off the wire or out of storage goes through.
 *
 * A locale arrives as an unvalidated string in three places — the profile the refresh returns, the
 * profile a sign-in returns, and the cached row on disk — and each of them used to spell the
 * membership test inline. That is one edit per locale, in files that do not mention i18n, and the
 * failure mode is silent: an unrecognized value falls back to the default, so a Russian account
 * whose narrowing was missed reads as Hebrew with nothing logged.
 */
export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value)
}

export function isRtlLocale(locale: Locale): boolean {
  return locale === 'he'
}

/**
 * BCP-47 tag `Intl` formatters key their Sunday-first/24-hour behavior off.
 *
 * 🔸 Assumption: the PRD requires Sunday-first only for `he-IL` (§13 "Hebrew (RTL) and English").
 * English is tagged `en-US`, whose own week convention is also Sunday-first. **Every other locale
 * takes the `-IL` region rather than its language's home one** — `ru-IL` not `ru-RU`, `it-IL` not
 * `it-IT`, `uk-IL` not `uk-UA` — because the audience is speakers of those languages living at
 * Israeli gates, so the calendar, the week and the time conventions that matter are Israel's, and
 * every home region here would render a Monday-first week beside a `WEEKDAY_ORDER` (`format.ts`)
 * that is Sunday-first by contract with `packages/core`'s schedule evaluator. Measured, not
 * assumed: `new Intl.Locale(t).getWeekInfo().firstDay` is 1 for `it-IT` and for `uk-UA`, and 7 for
 * `it-IL`, `uk-IL`, `ru-IL` and `he-IL`. All are well-formed BCP-47 tags; where ICU carries no
 * data for one, `Intl` falls back to the bare language and keeps its grammar, which is the half
 * that cannot be recovered any other way — `it-IL` resolves to `it` and still renders "2 ore fa"
 * and "ieri"; `uk-IL` resolves to `uk` and still renders "2 години тому", "учора", "17 серпня
 * 2026 р." and 24-hour time.
 */
export function intlTag(locale: Locale): string {
  if (locale === 'he') return 'he-IL'
  if (locale === 'ru') return 'ru-IL'
  if (locale === 'uk') return 'uk-IL'
  return locale === 'it' ? 'it-IL' : 'en-US'
}
